import { parse, stringify } from 'querystring';
import { anyPass, contains, equals, startsWith } from 'ramda';
import { RoutePath, RouteResource } from '../constants/Routes';
import { ExternalSource, MainSource, Source } from '../constants/Source';
import { SystemFolderId } from '../constants/SystemFolderId';
import { removeNilValues } from './object';

export const RouteBuilder = {
  EnterpriseTab: (tabName: string, folderId?: string, queryString?: string) =>
    `/${RouteResource.EnterpriseTab}/${tabName}${folderId ? `/${folderId}` : ''}${
      queryString ? `?${queryString}` : ''
    }`,
  Source: (source: Source, folderId?: string, itemId?: string, searchPart?: string) => {
    const queryString = stringify(removeNilValues({ selectedItem: itemId, ...parse(searchPart) }));

    return `/${RouteResource.Sources}/${source}${folderId ? `/${folderId}` : ''}${
      queryString ? `?${queryString}` : ''
    }`;
  },
  Search: (source: Source, searchQuery: string, folderId?: string) => {
    const searchQueryPart = searchQuery
      ? `?searchQuery=${encodeURIComponent(searchQuery)}&searchCacheKiller=${Date.now()}`
      : '';
    const folderIdPart = folderId ? `/${folderId}` : '';

    return `/${RouteResource.Search}/${source}${folderIdPart}${searchQueryPart}`;
  },
  ExternalSource: (externalSource: ExternalSource) =>
    `/${RouteResource.Sources}/${MainSource.SiteFiles}?openedExternal=${externalSource}`,
  Empty: () => RoutePath.Empty,
  All: () => RoutePath.ShowAll,
  UnknownError: () => RoutePath.UnknownError,
  Home: () => `/${RouteResource.Sources}/${RouteResource.Home}`,
  BoardsRoot: () => `/${RouteResource.Sources}/${RouteResource.Boards}`,
  AddFilesToBoardModal: () => `/${RouteResource.AddFilesToBoardModal}`,
};

export const RouteMatcher = {
  isEmpty: anyPass([startsWith<string>(RouteBuilder.Empty()), startsWith<string>(RouteResource.Empty)]),
  isSearch: anyPass([startsWith<string>(`/${RouteResource.Search}`), startsWith<string>(RouteResource.Search)]),
  isSearchEverywhere: anyPass([
    startsWith<string>(`/${RouteResource.Search}/all`),
    startsWith<string>(`${RouteResource.Search}/all`),
  ]),
  isPublicSearch: anyPass([
    startsWith<string>(`/${RouteResource.Search}/public`),
    startsWith<string>(`${RouteResource.Search}/public`),
  ]),
  isAll: startsWith<string>(RouteBuilder.All()),
  isUnknownError: startsWith<string>(RouteBuilder.UnknownError()),
  isHome: startsWith<string>(RouteBuilder.Home()),
  isBoardsRoot: anyPass([
    equals(RouteBuilder.BoardsRoot()),
    equals(RouteBuilder.Source(MainSource.Boards, SystemFolderId.Boards)),
  ]),
  isBoard: anyPass([
    startsWith<string>(`/{${RouteResource.Sources}/${MainSource.Boards}`),
    startsWith<string>(`${RouteResource.Sources}/${MainSource.Boards}`),
  ]),
  is: (source: MainSource, pathname: string): boolean => contains(`/${source}`, pathname),
  isAddFilesToBoardModal: equals(`/${RouteResource.AddFilesToBoardModal}`),
};
